import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { Activity, Clock, Repeat, Timer, Zap } from "lucide-react";
import { WorkoutContext } from "../../contexts/WorkoutContext";
import { TIMER_PRESETS } from "../../config/timers";
import useWorkoutPresets from "../../hooks/useWorkoutPresets";
import { describeTimer } from "../../utils/timerSummary";
import { Group, ItemRow, SummaryRow } from "../profile/ProfileParts";
import TimerSetupSheet, { PRESET_HAS_CONFIG } from "./TimerSetupSheet";

// Free training ("free") isn't listed: it's a way to train, not a timer, and is
// offered as Just train beside the other ways to start (#163).
export const QUICK_MODES = ["tabata", "hiit", "emom", "amrap", "stopwatch", "countdown"];

const ICONS = { free: Activity, tabata: Timer, hiit: Zap, emom: Repeat, amrap: Timer, stopwatch: Clock, countdown: Clock };
export const timerIcon = (mode) => ICONS[mode] || Timer;

const SHEET_CLOSE_DELAY_MS = 220;

const mergedSettings = (custom) => ({
  ...(TIMER_PRESETS[custom.mode]?.settings || {}),
  ...custom.settings,
});

// Saved timers, most recently used first, then newest.
export const orderSavedTimers = (presets = []) =>
  [...presets].sort((a, b) => {
    const at = (p) => new Date(p.last_used_at || 0).getTime();
    return at(b) - at(a) || new Date(b.created_at || 0) - new Date(a.created_at || 0);
  });

const DAY_MS = 24 * 60 * 60 * 1000;
export const describeLastUsed = (iso, now = new Date()) => {
  if (!iso) return "Not used yet";
  const startOf = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const days = Math.round((startOf(now) - startOf(new Date(iso))) / DAY_MS);
  if (days <= 0) return "Used today";
  if (days === 1) return "Used yesterday";
  if (days < 7) return `Used ${days} days ago`;
  if (days < 14) return "Used last week";
  return `Used ${new Date(iso).toLocaleDateString("en-AU", { day: "numeric", month: "short" })}`;
};

/**
 * Starting a timer, wherever it's offered (the Coach at rest and Session's
 * Timers sheet): open the setup sheet for a quick or saved timer, start it in
 * the Coach, and save, duplicate or delete saved timers (#109).
 */
export const useTimerLauncher = ({ onStarted } = {}) => {
  const { loadPresetIntoCoach, setView } = useContext(WorkoutContext);
  const { customPresets, savePreset, updatePreset, deletePreset, touchPreset } = useWorkoutPresets();

  const [open, setOpen] = useState(false);
  const [pending, setPending] = useState({ mode: null, id: null, name: null, settings: null });
  const cleanupRef = useRef(null);

  const cancelCleanup = useCallback(() => {
    if (cleanupRef.current !== null) {
      window.clearTimeout(cleanupRef.current);
      cleanupRef.current = null;
    }
  }, []);
  useEffect(() => () => cancelCleanup(), [cancelCleanup]);

  // Clear the sheet's subject only after it has animated closed.
  const close = useCallback(() => {
    setOpen(false);
    cancelCleanup();
    cleanupRef.current = window.setTimeout(() => {
      setPending({ mode: null, id: null, name: null, settings: null });
      cleanupRef.current = null;
    }, SHEET_CLOSE_DELAY_MS);
  }, [cancelCleanup]);

  const start = useCallback(
    (mode, settings, id) => {
      loadPresetIntoCoach(mode, settings);
      setView("coach");
      if (id) touchPreset(id);
      onStarted?.();
    },
    [loadPresetIntoCoach, setView, touchPreset, onStarted],
  );

  const openQuick = useCallback(
    (mode) => {
      if (!PRESET_HAS_CONFIG(mode)) {
        start(mode, { ...TIMER_PRESETS[mode].settings });
        return;
      }
      cancelCleanup();
      setPending({ mode, id: null, name: null, settings: null });
      setOpen(true);
    },
    [cancelCleanup, start],
  );

  const openSaved = useCallback(
    (custom) => {
      if (!PRESET_HAS_CONFIG(custom.mode)) {
        start(custom.mode, mergedSettings(custom), custom.id);
        return;
      }
      cancelCleanup();
      setPending({ mode: custom.mode, id: custom.id, name: custom.name, settings: mergedSettings(custom) });
      setOpen(true);
    },
    [cancelCleanup, start],
  );

  const handleSave = useCallback(
    async ({ id, name, mode, settings }) => {
      if (id) {
        await updatePreset(id, { name, settings });
      } else {
        const created = await savePreset({ name, mode, settings });
        setPending({ mode, id: created.id, name: created.name, settings });
      }
    },
    [updatePreset, savePreset],
  );

  const current = customPresets.find((p) => p.id === pending.id);

  const sheet = (
    <TimerSetupSheet
      open={open}
      mode={pending.mode}
      presetId={pending.id}
      presetName={pending.name}
      initialSettings={pending.settings}
      onClose={close}
      onConfirm={(config) => {
        const { mode, id } = pending;
        close();
        if (mode) start(mode, config, id);
      }}
      onSave={handleSave}
      onDuplicate={
        current
          ? () => savePreset({ name: `${current.name} (copy)`, mode: current.mode, settings: current.settings })
          : undefined
      }
      onDelete={
        current
          ? async () => {
              close();
              await deletePreset(current.id);
            }
          : undefined
      }
    />
  );

  return { openQuick, openSaved, savedTimers: orderSavedTimers(customPresets), sheet };
};

/** Quick timers and your saved timers, as rows that open the setup sheet. */
const TimersList = ({ launcher, quickNote = "No plan needed" }) => (
  <>
    <Group title="Quick timers" note={quickNote}>
      {QUICK_MODES.map((mode) => (
        <SummaryRow
          key={mode}
          Icon={timerIcon(mode)}
          label={TIMER_PRESETS[mode].label}
          value={describeTimer(mode, TIMER_PRESETS[mode].settings)}
          muted={mode === "stopwatch"}
          onClick={() => launcher.openQuick(mode)}
        />
      ))}
    </Group>
    {launcher.savedTimers.length > 0 && (
      <Group title="Your timers" note="Most recent first">
        {launcher.savedTimers.map((custom) => (
          <ItemRow
            key={custom.id}
            Icon={timerIcon(custom.mode)}
            title={custom.name}
            detail={[
              TIMER_PRESETS[custom.mode]?.label || custom.mode,
              describeTimer(custom.mode, mergedSettings(custom)),
              describeLastUsed(custom.last_used_at),
            ]
              .filter(Boolean)
              .join(" · ")}
            onClick={() => launcher.openSaved(custom)}
          />
        ))}
      </Group>
    )}
  </>
);

export default TimersList;
